import { FormulaError } from './errors.js';

export type TokenType =
  | 'NUMBER'
  | 'STRING'
  | 'FIELD_REF'
  | 'IDENT'
  | 'PLUS'
  | 'MINUS'
  | 'STAR'
  | 'SLASH'
  | 'LPAREN'
  | 'RPAREN'
  | 'LBRACKET'
  | 'RBRACKET'
  | 'COMMA'
  | 'EQ'
  | 'NEQ'
  | 'GT'
  | 'GTE'
  | 'LT'
  | 'LTE'
  | 'TRUE'
  | 'FALSE'
  | 'EOF';

export interface Token {
  type: TokenType;
  value: string;
  position: number;
}

export function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  let pos = 0;

  while (pos < input.length) {
    // Skip whitespace
    if (/\s/.test(input[pos]!)) {
      pos++;
      continue;
    }

    const start = pos;
    const ch = input[pos]!;

    // Single-char tokens
    if (ch === '+') { tokens.push({ type: 'PLUS', value: '+', position: start }); pos++; continue; }
    if (ch === '-') { tokens.push({ type: 'MINUS', value: '-', position: start }); pos++; continue; }
    if (ch === '*') { tokens.push({ type: 'STAR', value: '*', position: start }); pos++; continue; }
    if (ch === '/') { tokens.push({ type: 'SLASH', value: '/', position: start }); pos++; continue; }
    if (ch === '(') { tokens.push({ type: 'LPAREN', value: '(', position: start }); pos++; continue; }
    if (ch === ')') { tokens.push({ type: 'RPAREN', value: ')', position: start }); pos++; continue; }
    if (ch === '[') { tokens.push({ type: 'LBRACKET', value: '[', position: start }); pos++; continue; }
    if (ch === ']') { tokens.push({ type: 'RBRACKET', value: ']', position: start }); pos++; continue; }
    if (ch === ',') { tokens.push({ type: 'COMMA', value: ',', position: start }); pos++; continue; }

    // Two-char operators
    if (ch === '!' && pos + 1 < input.length && input[pos + 1] === '=') {
      tokens.push({ type: 'NEQ', value: '!=', position: start });
      pos += 2;
      continue;
    }
    if (ch === '>' && pos + 1 < input.length && input[pos + 1] === '=') {
      tokens.push({ type: 'GTE', value: '>=', position: start });
      pos += 2;
      continue;
    }
    if (ch === '<' && pos + 1 < input.length && input[pos + 1] === '=') {
      tokens.push({ type: 'LTE', value: '<=', position: start });
      pos += 2;
      continue;
    }
    if (ch === '>') { tokens.push({ type: 'GT', value: '>', position: start }); pos++; continue; }
    if (ch === '<') { tokens.push({ type: 'LT', value: '<', position: start }); pos++; continue; }
    if (ch === '=') { tokens.push({ type: 'EQ', value: '=', position: start }); pos++; continue; }

    // Number
    if (/[0-9]/.test(ch) || (ch === '.' && pos + 1 < input.length && /[0-9]/.test(input[pos + 1]!))) {
      let num = '';
      while (pos < input.length && /[0-9]/.test(input[pos]!)) {
        num += input[pos]!;
        pos++;
      }
      if (pos < input.length && input[pos] === '.') {
        num += '.';
        pos++;
        while (pos < input.length && /[0-9]/.test(input[pos]!)) {
          num += input[pos]!;
          pos++;
        }
      }
      tokens.push({ type: 'NUMBER', value: num, position: start });
      continue;
    }

    // String literal
    if (ch === '"') {
      pos++; // skip opening quote
      let str = '';
      while (pos < input.length && input[pos]! !== '"') {
        if (input[pos]! === '\\' && pos + 1 < input.length) {
          pos++;
          str += input[pos]!;
        } else {
          str += input[pos]!;
        }
        pos++;
      }
      if (pos >= input.length) {
        throw new FormulaError('Unterminated string literal', 'syntax_error', start);
      }
      pos++; // skip closing quote
      tokens.push({ type: 'STRING', value: str, position: start });
      continue;
    }

    // Field reference {slug}
    if (ch === '{') {
      pos++; // skip {
      let slug = '';
      while (pos < input.length && input[pos]! !== '}') {
        slug += input[pos]!;
        pos++;
      }
      if (pos >= input.length) {
        throw new FormulaError('Unterminated field reference', 'syntax_error', start);
      }
      pos++; // skip }
      if (!slug) {
        throw new FormulaError('Empty field reference', 'syntax_error', start);
      }
      tokens.push({ type: 'FIELD_REF', value: slug, position: start });
      continue;
    }

    // Identifier (function name or boolean keyword)
    if (/[a-zA-Z_]/.test(ch)) {
      let ident = '';
      while (pos < input.length && /[a-zA-Z0-9_]/.test(input[pos]!)) {
        ident += input[pos]!;
        pos++;
      }
      const upper = ident.toLowerCase();
      if (upper === 'true') {
        tokens.push({ type: 'TRUE', value: 'true', position: start });
      } else if (upper === 'false') {
        tokens.push({ type: 'FALSE', value: 'false', position: start });
      } else {
        tokens.push({ type: 'IDENT', value: ident, position: start });
      }
      continue;
    }

    throw new FormulaError(`Unexpected character: '${ch}'`, 'syntax_error', pos);
  }

  tokens.push({ type: 'EOF', value: '', position: pos });
  return tokens;
}
